import React, { FC, useState, useMemo } from "react";
import { useAppContext } from "AppContext";
import { parseFilterDate, toFilterDateString } from "utils/filterDates";
import { useLocale } from "i18n/locale";

interface DatePickerEuropeaProps {
  onClose: () => void;
}

const DAYS_EN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;
const DAYS_FR = ["DIM", "LUN", "MAR", "MER", "JEU", "VEN", "SAM"] as const;

const DatePickerEuropea: FC<DatePickerEuropeaProps> = ({ onClose }) => {
  const { t, locale } = useLocale();
  const { filterState, updateFilterState } = useAppContext();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [baseMonth, setBaseMonth] = useState(() => {
    const d = parseFilterDate(filterState?.checkInDate);
    if (d) {
      return { year: d.getFullYear(), month: d.getMonth() };
    }
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  const [startDate, setStartDate] = useState<Date | null>(() =>
    parseFilterDate(filterState?.checkInDate)
  );
  const [endDate, setEndDate] = useState<Date | null>(() =>
    parseFilterDate(filterState?.checkOutDate)
  );

  const [selecting, setSelecting] = useState<"start" | "end">(
    startDate && !endDate ? "end" : "start"
  );

  const nextMonth = (y: number, m: number) =>
    m === 11 ? { year: y + 1, month: 0 } : { year: y, month: m + 1 };

  const prevMonth = (y: number, m: number) =>
    m === 0 ? { year: y - 1, month: 11 } : { year: y, month: m - 1 };

  const second = nextMonth(baseMonth.year, baseMonth.month);

  const daysIn = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const firstDow = (y: number, m: number) => new Date(y, m, 1).getDay();

  const fmtMonthYear = (y: number, m: number) =>
    new Date(y, m, 1).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
      month: "long",
      year: "numeric",
    });

  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const inRange = (d: Date) => {
    if (!startDate || !endDate) return false;
    return d > startDate && d < endDate;
  };

  const isPast = (d: Date) => d < today;

  const handleDayClick = (d: Date) => {
    if (isPast(d)) return;
    if (selecting === "start") {
      setStartDate(d);
      setEndDate(null);
      setSelecting("end");
    } else {
      if (d <= startDate!) {
        setStartDate(d);
        setEndDate(null);
        setSelecting("end");
      } else {
        setEndDate(d);
        setSelecting("start");
        // Selecting the check-out date launches the search directly: commit both
        // dates to the shared filter state (which triggers the listing refetch)
        // and close the panel — there is no separate "Continue" step.
        updateFilterState("checkInDate", toFilterDateString(startDate) ?? "");
        updateFilterState("checkOutDate", toFilterDateString(d) ?? "");
        onClose();
      }
    }
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setSelecting("start");
    // Also clear the shared filter state, not just this component's local
    // state. Otherwise the reset is only visual: the navbar keeps the old
    // label and reopening the calendar re-seeds startDate/endDate from
    // filterState. Clearing the dates here updates the label and refetches
    // the listing without a date filter.
    updateFilterState("checkInDate", null);
    updateFilterState("checkOutDate", null);
  };

  const buildCells = (y: number, m: number): (Date | null)[] => {
    const cells: (Date | null)[] = [];
    const fd = firstDow(y, m);
    for (let i = 0; i < fd; i++) cells.push(null);
    for (let d = 1; d <= daysIn(y, m); d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const renderMonth = (y: number, m: number) => {
    const cells = buildCells(y, m);
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }

    return (
      <div className="w-[308px]">
        {/* Day headers */}
        <div className="grid grid-cols-7 px-[24px]">
          {(locale === "fr" ? DAYS_FR : DAYS_EN).map((day) => (
            <div key={day} className="flex justify-center p-[4px]">
              <span
                className="text-[10px] text-[#445162] uppercase"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
              >
                {day}
              </span>
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="flex flex-col gap-[16px] mt-[16px]">
          {rows.map((row, ri) => (
            <div key={ri} className="grid grid-cols-7 px-[24px]">
              {row.map((date, ci) => {
                if (!date) {
                  return <div key={ci} className="h-[24px]" />;
                }

                const isStart = !!(startDate && same(date, startDate));
                const isEnd = !!(endDate && same(date, endDate));
                const ranged = inRange(date);
                const past = isPast(date);

                // For range bg: which parts of the cell get the bg
                let rangeBg: "full" | "right" | "left" | "none" = "none";
                if (ranged) rangeBg = "full";
                else if (isStart && endDate) rangeBg = "right";
                else if (isEnd && startDate) rangeBg = "left";

                return (
                  <div
                    key={ci}
                    className="relative flex items-center justify-center h-[24px]"
                  >
                    {/* Range highlight behind the day circle */}
                    {rangeBg === "full" && (
                      <div className="absolute inset-0 bg-[#faf7f1]" />
                    )}
                    {rangeBg === "right" && (
                      <div className="absolute inset-y-0 left-1/2 right-0 bg-[#faf7f1]" />
                    )}
                    {rangeBg === "left" && (
                      <div className="absolute inset-y-0 left-0 right-1/2 bg-[#faf7f1]" />
                    )}

                    <button
                      onClick={() => handleDayClick(date)}
                      disabled={past}
                      className={`relative flex items-center justify-center w-[24px] h-[24px] shrink-0 border-0 p-0 ${
                        past ? "cursor-default" : "cursor-pointer"
                      }`}
                      style={{
                        background:
                          isStart || isEnd ? "#B88333" : "transparent",
                        borderRadius: isStart || isEnd ? "50%" : undefined,
                      }}
                    >
                      <span
                        className="text-[14px] text-center"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          color:
                            isStart || isEnd
                              ? "#ffffff"
                              : past
                              ? "#d6d6d6"
                              : "#01152E",
                        }}
                      >
                        {date.getDate()}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className="bg-white rounded-[12px] py-[24px] flex flex-col z-50"
      style={{
        boxShadow: "0px 0px 15px rgba(0,0,0,0.2)",
        width: "616px",
      }}
    >
      {/* Month headers + nav arrows */}
      <div className="flex items-center px-[24px] mb-[10px]">
        <button
          onClick={() =>
            setBaseMonth(prevMonth(baseMonth.year, baseMonth.month))
          }
          className="cursor-pointer bg-transparent border-0 p-[4px] flex items-center"
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
            <path
              d="M7 1L1 7L7 13"
              stroke="#01152E"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex flex-1">
          <p
            className="flex-1 text-[16px] text-center text-[#01152E]"
            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
          >
            {fmtMonthYear(baseMonth.year, baseMonth.month)}
          </p>
          <p
            className="flex-1 text-[16px] text-center text-[#01152E]"
            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
          >
            {fmtMonthYear(second.year, second.month)}
          </p>
        </div>

        <button
          onClick={() =>
            setBaseMonth(nextMonth(baseMonth.year, baseMonth.month))
          }
          className="cursor-pointer bg-transparent border-0 p-[4px] flex items-center"
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
            <path
              d="M1 1L7 7L1 13"
              stroke="#01152E"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Two calendars side by side */}
      <div className="flex">
        {renderMonth(baseMonth.year, baseMonth.month)}
        {renderMonth(second.year, second.month)}
      </div>

      {/* Bottom buttons — the search now launches automatically when the
          check-out date is picked, so only "Clear all" remains. */}
      <div className="flex items-center justify-end px-[24px] mt-[24px]">
        <button
          onClick={handleClear}
          className="h-[30px] px-[12px] rounded-[20px] border border-[#d6d6d6] bg-white flex items-center justify-center cursor-pointer"
        >
          <span
            className="text-[12px] text-[#01152E] whitespace-nowrap"
            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
          >
            {t("filters.clearAll")}
          </span>
        </button>
      </div>
    </div>
  );
};

export default DatePickerEuropea;
